import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTheme } from "../../../../shared/common/ThemeContext";
import { usePlayersPerf } from "../../../../shared/common/PlayersPerfContext";
import { smartDecode } from "../../../../shared/common/hashid.js";
import { useTranslation } from 'react-i18next'

function getLabelFallback(segment, LABELS) {
  if (!segment) return LABELS.overview;
  const key = segment.toLowerCase();
  if (LABELS[key]) return LABELS[key];
  if (/^\d+$/.test(segment)) return "Detail";
  if (/^[0-9a-f-]{20,}$/i.test(segment)) return "Player";
  if (segment.length >= 10 && /^[a-zA-Z0-9]+$/.test(segment)) return "Player";
  if (/^[a-z0-9-]+$/.test(segment) && segment.includes("-")) {
    return segment.split("-").filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

export default function NavigationHeader() {
  const { i18n, t } = useTranslation()
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { players } = usePlayersPerf() || {};

  const LABELS = {
    overview: t('nav.overview'),
    session: t('nav.session'),
    settings: t('nav.settings'),
    calendar: t('nav.calendar'),
    playersperfs: t('nav.players'),
    players: t('nav.player'),
    survey: t('nav.survey'),
    reports: t('nav.reports'),
    club: t('nav.club'),
    collectif: t('nav.collectif'),
    "update-players-infos": t('nav.players_infos'),
  }

  const segments = location.pathname.split("/").filter(Boolean);
  const canGoBack = window.history.length > 1;

  const handleBack = () => {
    if (canGoBack) { navigate(-1); } else { navigate("/overview"); }
  };

  const isPlayersDetailRoute = segments[0] === "players" && segments.length === 2;
  const isPlayerInfosRoute = segments[0] === "update-players-infos" && segments.length === 2;
  const hasPlayerIdRoute = isPlayersDetailRoute || isPlayerInfosRoute;
  const routeId = hasPlayerIdRoute ? segments[1] : null;
  const decodedPlayerId = routeId ? smartDecode(routeId) : null;

  const player = decodedPlayerId && Array.isArray(players)
    ? players.find((p) => String(p.id) === String(decodedPlayerId))
    : null;

  const playerFullName = player
    ? `${player.first_name ?? ""} ${player.last_name ?? ""}`.trim()
    : null;

  const lastSegment = segments[segments.length - 1] || "overview";

  const pageTitle = (() => {
    if (hasPlayerIdRoute && playerFullName) return playerFullName;
    return getLabelFallback(lastSegment, LABELS);
  })();

  const pathText = segments.length === 0
    ? t('nav.overview')
    : segments.map((seg, idx) => {
        if (hasPlayerIdRoute && idx === 1 && playerFullName) return playerFullName;
        return getLabelFallback(seg, LABELS);
      }).join(" / ");

  const containerCls = `sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-3 backdrop-blur-sm pointer-events-none ${isDarkMode ? "bg-gray-900/95" : "bg-gray-100/95"}`;
  const innerCls = `flex w-full items-center justify-between gap-3 pointer-events-auto`;
  const backBtnCls = `inline-flex items-center justify-center h-9 w-9 rounded-full shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${isDarkMode ? "bg-gray-900/80 text-gray-100 hover:bg-gray-800/90 ring-offset-gray-950" : "bg-white/90 text-gray-900 hover:bg-gray-100 ring-offset-white border border-gray-200/70"}`;
  const titleCls = `text-sm sm:text-base font-semibold ${isDarkMode ? "text-gray-50" : "text-gray-900"}`;
  const subtitleCls = `text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;

  return (
    <header className={containerCls} aria-label="Page navigation">
      <div className={innerCls}>
        <button type="button" onClick={handleBack} className={backBtnCls} aria-label="Go back">
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-col">
          <span className={titleCls}>{pageTitle}</span>
          <span className={`${subtitleCls} truncate`}>{pathText}</span>
        </div>
        
        <button
          onClick={() => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')}
          className={`inline-flex items-center justify-center h-9 px-3 rounded-full text-xs font-semibold transition-all shadow-sm ${isDarkMode ? "bg-gray-800 text-gray-100 hover:bg-gray-700 border border-gray-700" : "bg-white text-gray-900 hover:bg-gray-100 border border-gray-200"}`}
        >
          {i18n.language === 'fr' ? 'EN' : 'FR'}
        </button> 
      </div>
    </header>
  );
}